import { api } from '../utils/api';

export interface LineSnapshot {
  line_item_id?: string;
  estimate_id?: string;
  source_code: string;
  description: string;
  unit: string;
  quantity: string;
  extended_price: string;
  coverage_section?: string;
  building: string | null;
  location_unit?: string | null;
  room: string | null;
  // Trade/catalog dimensions the normalizer derives (projected read-only by the backend
  // `_line_snapshot` for the workbench's data-driven REFINE chips). Non-money categorical.
  // `category` is the canonical trade bucket (degenerate == source_code on estimates the
  // taxonomy can't resolve — the chip logic suppresses it then); `actions` is the activity
  // set (INS/SUP/RMV…). Optional: older persisted payloads predate the projection.
  category?: string | null;
  selector?: string | null;
  actions?: string[] | null;
  requires_review: boolean;
  confidence?: number;
}

export interface MatchDetail {
  match_id?: string;
  canonical_key: string[];
  match_type: string;
  confidence: number;
  a_codes?: string[];
  b_codes?: string[];
  quantity_ok: boolean;
  requires_review: boolean;
  linked_line_items?: string[];
  a_lines: LineSnapshot[];
  b_lines: LineSnapshot[];
  // WTFK auto-reconciliation ROUTER (backend matching_bridge._classify_auto_reconciliation, reusing
  // AutoReconciliationService rules). Routes a pair off/onto the review pile — it does NOT elect a
  // payable dollar. 'auto_approved' = exact 0% / ≤5% variance / ≥0.9 fuzzy no qty-price-fight;
  // 'needs_review' = variance >5%, quantity-match-price-diff, weak fuzzy, qty mismatch. Optional:
  // older persisted payloads predate the router (frontend then shows all pairs in the main queue).
  auto_reconciliation_status?: 'auto_approved' | 'needs_review' | string | null;
  auto_reconciliation_rule?: string | null;
}

export interface VarianceDetail {
  variance_id?: string;
  category: string;
  subject: string[];
  amount: string;
  abs_amount: string;
  direction: 'debit' | 'credit' | string;
  root_cause?: string;
  attributed_to: string | null;
  requires_review: boolean;
  confidence?: number;
  evidence?: {
    derived_from: string[];
    line_items: string[];
  };
  unit?: string;
  materiality_rank: number;
  share_of_gross?: number;
  // Lane-partitioned scope (backend property_report._variances, from the scope engine's
  // classifiers stored on the graph node). Present on SCOPE variances only — price/quantity
  // variances have no coverage lane, so both are null there. Non-money categorical fields:
  // coverage_lane ∈ roof | building_envelope | unit_interior | association_common | unknown;
  // responsibility ∈ association | unit_owner | unknown (condo allocation — 'unknown' is a real
  // flagged-for-review state, never guessed).
  coverage_lane?: string | null;
  responsibility?: string | null;
  // how `responsibility` was derived: 'explicit' (a header) | 'code_matrix' (Xactimate code) |
  // 'lane_default' (inferred from a per-claim condo convention, NOT read from the estimate) | 'none'.
  // Surfaced so a reviewer can tell an inferred allocation from one read off the line.
  responsibility_basis?: string | null;
}

// Reconciliation readiness (backend property_readiness.PropertyReadinessEvaluator, projected onto
// /result by matching_bridge._summarize). The "is this reconciliation settlement-review-ready" verdict
// — NOT a judgement of the deterministic match itself. score is 0–100; status is the rollup. Each check
// carries a human-readable `detail` (the substance) + an optional 0–1 `metric`; `blocking` marks a check
// that gates readiness (only `estimates_ingested` is blocking today). `gaps` are the failed checks with a
// severity — a subset of `checks`, surfaced for triage.
export interface ReadinessCheck {
  name: string;
  passed: boolean;
  blocking: boolean;
  detail: string;
  metric?: number | null;
}

export interface ReadinessGap {
  check: string;
  detail: string;
  severity: string;
}

export interface Readiness {
  claim_id?: string;
  score: number;
  status: 'ready' | 'partial' | 'blocked' | string;
  checks: ReadinessCheck[];
  gaps?: ReadinessGap[];
}

// PROPOSE-ONLY sketch geometry (backend reconciliation/ingest/sketch_adapter.py), lifted verbatim
// from the estimate sketch pages. Values are the raw strings from the PDF (room measures keep their
// unit e.g. "262.77 SF"; roof measures are the bare number, the *_lf field name carrying the unit).
// A READ-ONLY review artifact — it never feeds the matcher and no field here is editable in the UI.
export interface SketchArea {
  area_id: string;
  estimate_id: string;
  name: string;
  height?: string | null;
  page?: number;
  provenance?: { source?: string; page?: number; verbatim?: boolean } | null;
  // room surfaces
  walls_sf?: string | null;
  ceiling_sf?: string | null;
  floor_sf?: string | null;
  floor_perimeter_lf?: string | null;
  ceiling_perimeter_lf?: string | null;
  // roof geometry (present ⇒ this Area is a roof face)
  number_of_squares?: string | null;
  ridge_length_lf?: string | null;
  hip_length_lf?: string | null;
  valley_length_lf?: string | null;
  eave_length_lf?: string | null;
  rake_length_lf?: string | null;
}
export interface SketchAreas {
  carrier: SketchArea[];
  expert: SketchArea[];
}

// PROPOSE-ONLY fuzzy/dictionary candidate suggestions (backend crosswalk_engine.candidate_pairs,
// projected onto /result). Residue near-misses scored in the band [floor, auto-cutoff) — a plausible
// pair a human adjudicates (Match / No-match) on the workbench. NEVER applied to matches/variances/
// dollars: this is a review artifact only, the deterministic 97/699/$ result is untouched. Generated
// PER location bucket, so a_item_id and b_item_id always share a (building, unit, room) bucket. Look up
// the full line via `line_items['line_item:' + a_item_id]`.
export interface MatchCandidate {
  a_item_id: string;
  b_item_id: string;
  score: number;                 // token_sort_ratio, in [floor, 84)
  confidence: number;            // score / 100
  category_match: boolean;       // both sides resolved the SAME canonical category
  canonical_category: string;
  quantity_ok: boolean | null;   // null when a base quantity could not be resolved
  a_actions: string[];           // ⊆ {SUP, INS, RMV}
  b_actions: string[];
  actions_ok: boolean;           // same action set both sides (false ⇒ e.g. install-vs-remove)
  a_description: string;
  b_description: string;
  source: 'fuzzy_dictionary';
}

export interface Summary {
  claim_id: string;
  estimates: number;
  line_items: number;
  matches: number;
  monetary_variance_count: number;
  price_variance_count: number;
  quantity_variance_count: number;
  scope_variance_count: number;
  net_dollar_variance: string;
  gross_dollar_variance: string;
  net_dollar_variance_display: string;
  open_items: number;
  node_counts?: Record<string, number>;
}

export interface TradeSummary {
  trades?: Array<{
    trade: string;
    net_variance: string;
    variance_count: number;
    requires_review: number;
  }>;
  distinct_trade_count?: number;
  gc_op_warranted: boolean;
  op_variance: string | null;
  op_variance_note: string | null;
}

export interface Meta {
  run_at: string;
  run_by: string;
  carrier_estimate_id: string;
  expert_estimate_id: string;
  input_hash: string;
  synthetic: boolean;
}

export interface PropertyReconciliation {
  report_id: string;
  org_id?: string;
  claim_id: string;
  semantic_enabled: boolean;
  carrier_line_count: number;
  expert_line_count: number;
  match_count: number;
  matches_by_type: Record<string, number>;
  variance_count: number;
  scope_variance_count: number;
  scope_lanes: Record<string, number>;
  readiness?: Readiness | null;
  sketch_areas?: SketchAreas | null;   // A — propose-only room+roof geometry, read-only in the UI
  match_candidates?: MatchCandidate[] | null;   // MATCH-S1 — propose-only near-miss candidates (Match/No-match tray)
  summary?: Summary | null;
  trade_summary?: TradeSummary | null;
  domains_run?: Record<string, unknown>;
  domain_status?: Record<string, unknown>;
  matches_detail: MatchDetail[];
  variances_detail: VarianceDetail[];
  line_items: Record<string, LineSnapshot>;
  _meta: Meta;
  [key: string]: unknown;
}

export async function getPropertyReconciliation(
  reportId: string,
): Promise<PropertyReconciliation | null> {
  try {
    const data = await api.get<PropertyReconciliation>(
      '/api/v1/reconciliation/property-match/result/' + reportId,
      { showErrorToast: false, timeoutMs: 30000 },
    );
    return data;
  } catch (err: any) {
    if (err?.status === 404 || err?.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

// ── Step C — human election of the property L_PROP payable (fixture only, no dollars entered) ──────
// The accountant picks a SIDE per matched pair (or a whole-side bulk choice); the deterministic
// property engine computes L_PROP. NO dollar is ever sent — only a side. Backend keys per-pair
// choices by match_id (backend/app/api/v1/property_reconciliation.py). Fixture-clone only.

export type ElectionSide = 'contractor' | 'adjuster';

export interface ElectionRequest {
  choice: ElectionSide | 'mixed';
  // Required for 'mixed' — a pick for EVERY two-sided pair, keyed by match_id. Never sent for a
  // whole-side bulk choice (the backend 422s a bulk choice that carries per-pair picks).
  line_elections?: Record<string, ElectionSide>;
  // Bind to the exact reconciliation the human saw — both from `_meta` of the loaded /result blob.
  input_hash: string;
  run_at: string;
}

export interface ElectionSideTotals {
  contractor: number;
  adjuster: number;
}

export interface ElectionUnmatched {
  adjuster: { n: number; total: number };
  contractor: { n: number; total: number };
}

// ISO CP 00 10 §D coverage applied to the elected figure — every dollar engine-computed. The UI
// renders this as the derivation (gross → less deductible → cap → net) so a capped/reduced payable
// is never a bare number without its basis.
export interface ElectionCoverage {
  gross: number;
  deductible_nominal: number | null;
  deductible_applied: number;
  floored_at_zero: boolean;
  limit: number | null;
  capped_at_limit: boolean;
  net: number;
  order_of_operations: string;
  deductible_basis: string;
  limit_basis: string;
  limit_scope: string;
  bi_ledger_dollar_deductible: number;
  basis: string;
  holdback_re_deduction: boolean;
  notes: string[];
}

// The PATCH response: every dollar is engine-computed / a sum of stored lines — never client math.
export interface ElectionResult {
  report_id: string;
  choice: ElectionSide | 'mixed';
  elected_line_count: number;
  elected_payable_rcv: number;
  elected_payable_acv: number | null;
  acv_basis: 'elected' | 'absent' | string;
  per_side_totals: ElectionSideTotals;
  unmatched_included: ElectionUnmatched;
  // gross = elected RCV before coverage; l_prop_payable = NET after the ISO §D deductible + limit.
  gross_elected_rcv: number;
  coverage: ElectionCoverage;
  l_prop_payable: number | null;
  l_prop_basis: string | null;
  auto_pick_high: false;
  input_hash: string;
  elected_at: string;
  persisted: boolean;
}

// The persisted election record (GET), plus a `stale` marker set by the backend when the
// reconciliation has been re-run since this election was made.
export interface PersistedElection extends Partial<ElectionResult> {
  provenance?: string;
  basis?: string;
  line_elections?: Record<string, ElectionSide>;
  run_at?: string | null;
  elected_line_ids?: Array<string | null>;
  elected_by?: string | null;
  elector_role?: string | null;
  stale?: boolean;
  [key: string]: unknown;
}

export async function patchPropertyElection(
  reportId: string,
  body: ElectionRequest,
): Promise<ElectionResult> {
  return api.patch<ElectionResult>(
    '/api/v1/reconciliation/property-match/election/' + reportId,
    body,
  );
}

export async function getPropertyElection(
  reportId: string,
): Promise<PersistedElection | null> {
  try {
    return await api.get<PersistedElection>(
      '/api/v1/reconciliation/property-match/election/' + reportId,
      { showErrorToast: false, timeoutMs: 30000 },
    );
  } catch (err: any) {
    if (err?.status === 404 || err?.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

// ── MATCH-S3 — Match / No-match overlay over the propose-only candidate tray ──────────────────────
// A reversible human decision (matched | rejected) on a candidate pair, stored on the DRAFT — NEVER
// applied to the reconciliation or the L_PROP election. The GET returns the persisted records (each
// flagged stale/orphaned) plus a GET-only derived preview whose gross_abs_delta_rcv is NOT a payable.
export type MatchDecision = 'matched' | 'rejected';

export interface MatchOverlayRequest {
  a_item_id: string;
  b_item_id: string;
  decision: MatchDecision;
  input_hash: string;
  run_at: string;
}

export interface MatchOverlayRecord {
  a_item_id: string;
  b_item_id: string;
  decision: MatchDecision;
  score_seen?: number | null;
  decided_by?: string | null;
  decided_role?: string | null;
  decided_at?: string | null;
  input_hash?: string | null;
  run_at?: string | null;
  _stale?: boolean;      // reconciliation re-run since this decision → NOT applied to the preview
  _orphaned?: boolean;   // pair no longer a current candidate → NOT applied to the preview
}

export interface MatchOverlayView {
  matched_pairs: Array<{
    a_item_id: string; b_item_id: string;
    a_rcv: string | null; b_rcv: string | null; delta_rcv: string | null;
    derived: true; source: 'human_overlay';
  }>;
  matched_count: number;
  dollarized_count: number;
  gross_abs_delta_rcv: string;   // informational |Δ| sum — NEVER an L_PROP figure
  note: string;
}

export interface MatchOverlayState {
  report_id: string;
  overlay: MatchOverlayRecord[];
  overlay_view: MatchOverlayView;
  input_hash?: string | null;
  run_at?: string | null;
}

export async function patchMatchOverlay(
  reportId: string,
  body: MatchOverlayRequest,
): Promise<{ report_id: string; overlay_count: number; record: MatchOverlayRecord }> {
  return api.patch(
    '/api/v1/reconciliation/property-match/match-overlay/' + reportId,
    body,
    { showErrorToast: false },
  );
}

export async function getMatchOverlay(
  reportId: string,
): Promise<MatchOverlayState | null> {
  try {
    return await api.get<MatchOverlayState>(
      '/api/v1/reconciliation/property-match/match-overlay/' + reportId,
      { showErrorToast: false },
    );
  } catch (err: any) {
    if (err?.status === 404 || err?.response?.status === 404) return null;
    throw err;
  }
}

// ── Step B — advisory per-pair review-routing recommendation (read-only; the human still elects) ───
// Routes attention: per two-sided pair, the kind of doubt + a resolving next action + the engine's
// cited variance. proposed_side is ALWAYS null (no engine-grounded side basis for a matched pair);
// it is a HINT that pre-fills nothing — the human still elects via PATCH /election.
export type ElectionBasis =
  | 'uncertain_pairing'
  | 'undecidable_units'
  | 'quantity_dispute'
  | 'price_dispute'
  | 'no_dispute';
export type ElectionNextAction =
  | 'confirm_pairing'
  | 'establish_unit_basis'
  | 'obtain_measurement'
  | 'obtain_pricing_support'
  | 'none';

export interface ElectionRecommendationItem {
  match_id: string;
  // Always null — the backend type-locks it (the money-fence forbids advising a side for a pair).
  proposed_side: ElectionSide | null;
  basis: ElectionBasis;
  next_action: ElectionNextAction;
  cite: string;
}

export interface ElectionRecommendation {
  report_id: string;
  input_hash: string | null;
  run_at: string | null;
  recommendations: ElectionRecommendationItem[];
}

export async function getElectionRecommendation(
  reportId: string,
): Promise<ElectionRecommendation | null> {
  try {
    return await api.get<ElectionRecommendation>(
      '/api/v1/reconciliation/property-match/election/recommendation/' + reportId,
      { showErrorToast: false },
    );
  } catch (err: any) {
    if (err?.status === 404 || err?.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

// ── Slice 2 — property CAP confirm/reject (parser PROPOSES, a human CONFIRMS the live field) ───────
// The parser/LLM only ever write data.policy_proposals. The LIVE data.policy.property_limit /
// property_deductible (what the engine + election wrap read) are set ONLY by the attested confirm PATCH.
// No dollar is invented client-side; the human confirms a proposed candidate (or leaves it → uncapped).

export interface PolicyCapCandidate {
  value: number;
  cite: string | null;
  confidence: number;
  source: string;
  source_file_id?: string | null;
  proposed_at?: string;
}

export interface PolicyCapProposal {
  candidates: PolicyCapCandidate[];
  status: 'proposed' | 'confirmed' | 'rejected' | string;
  rejection?: Record<string, unknown>;
}

export interface PolicyCapState {
  report_id: string;
  proposals: {
    property_limit?: PolicyCapProposal;
    property_deductible?: PolicyCapProposal;
  };
  // LIVE human-confirmed caps the engine reads; null = "no property limit on file" (payable uncapped).
  live: {
    property_limit: number | null;
    property_deductible: number | null;
  };
  confirmations: Record<string, Record<string, unknown>>;
}

export type PolicyCapField = 'property_limit' | 'property_deductible';

export async function getPolicyCap(reportId: string): Promise<PolicyCapState | null> {
  try {
    return await api.get<PolicyCapState>(
      '/api/v1/reconciliation/property-match/policy-cap/' + reportId,
      { showErrorToast: false },
    );
  } catch (err: any) {
    if (err?.status === 404 || err?.response?.status === 404) return null;
    throw err;
  }
}

// Confirm a proposed candidate → the LIVE cap field. `fromCandidateIndex` promotes a proposal (no dollar
// typed); `value` is an explicit human override. Exactly one. A property_deductible confirmed atop a live
// BI dollar deductible 409s unless biDollarDeductibleAcknowledged (Gate D — never a silent double-apply).
export async function confirmPolicyCap(
  reportId: string,
  field: PolicyCapField,
  opts: {
    fromCandidateIndex?: number;
    value?: number;
    // TOCTOU guard: the candidate dollar the card DISPLAYED. If a re-parse changed the candidate at
    // that index, the backend 409s instead of promoting a value the human never saw. Sent with a candidate.
    expectedValue?: number;
    biDollarDeductibleAcknowledged?: boolean;
  },
): Promise<{ report_id: string; field: string; value: number; confirmation: Record<string, unknown> }> {
  const body: Record<string, unknown> = { field, action: 'confirm' };
  if (opts.fromCandidateIndex !== undefined) body.from_candidate_index = opts.fromCandidateIndex;
  if (opts.value !== undefined) body.value = opts.value;
  if (opts.expectedValue !== undefined) body.expected_value = opts.expectedValue;
  if (opts.biDollarDeductibleAcknowledged) body.bi_dollar_deductible_acknowledged = true;
  // showErrorToast:false — the card surfaces 409 (Gate D / TOCTOU) and 403 inline; a global toast would double it.
  return api.patch('/api/v1/reconciliation/property-match/policy-cap/' + reportId, body, { showErrorToast: false });
}

// Reject a proposal: the LIVE field stays EMPTY (payable uncapped, "no property limit on file"). No dollar.
export async function rejectPolicyCap(
  reportId: string,
  field: PolicyCapField,
): Promise<{ report_id: string; field: string; action: 'reject'; rejection: Record<string, unknown> }> {
  return api.patch(
    '/api/v1/reconciliation/property-match/policy-cap/' + reportId,
    { field, action: 'reject' },
    { showErrorToast: false },
  );
}

// ── WTFK-C — async MiniLM AI-suggestion tray (SEPARATE from the S1/S3 fuzzy candidate tray) ─────────
// Suggestions come from an async clustering job on the isolated matching worker: POST generate → poll
// progress → GET the ranked list. Match/No-match persists to a SEPARATE draft overlay
// (suggestion-overlay), NEVER the reconciliation blob or the S1/S3 candidate overlay. Gated by the
// backend RECON_MATCHING_ENABLED — generate 503s when off (surface it inline; do not toast).
export interface AiSuggestion {
  a_item_id: string;
  b_item_id: string;
  score: number | null;        // cluster match_score (0–100), or null if the worker emitted none
  keywords: string[];
  source: 'minilm_cluster';
}

export interface AiSuggestionsGenerated {
  report_id: string;
  job_id: string | null;       // null ⇒ no two-sided residue to cluster (see `note`)
  carrier_residue: number;
  expert_residue: number;
  input_hash?: string | null;
  run_at?: string | null;
  note?: string;
}

export type AiJobStatus = 'IN_PROGRESS' | 'COMPLETE' | 'ERROR' | string;

export interface AiSuggestionsProgress {
  report_id: string;
  job_id: string;
  status: AiJobStatus;
  total_batches?: number;
  done_batches?: number;
  error_batches?: string[];
  pending_batches?: number;
  percent_complete?: number;
}

export interface AiSuggestionsList {
  report_id: string;
  job_id: string | null;
  suggestions: AiSuggestion[];
  count: number;
  note?: string;
}

export interface SuggestionDecisionItem {
  a_item_id: string;
  b_item_id: string;
  decision: MatchDecision;     // 'matched' | 'rejected'
}

export interface SuggestionDecisionsRequest {
  job_id: string;
  input_hash: string;
  run_at: string;
  items: SuggestionDecisionItem[];   // 1–200; all-or-nothing on the backend
}

export interface SuggestionOverlayRecord extends MatchOverlayRecord {
  source?: string;                   // 'minilm_cluster'
  job_id?: string;
  keywords_seen?: string[] | null;
}

export interface SuggestionOverlayState {
  report_id: string;
  overlay: SuggestionOverlayRecord[];
  overlay_view: MatchOverlayView & { source_population?: string };
  input_hash?: string | null;
  run_at?: string | null;
}

const _AI = '/api/v1/reconciliation/property-match/ai-suggestions/';

// Enqueue a clustering job over the reconciliation residue. 503 when RECON_MATCHING_ENABLED is off.
export async function generateAiSuggestions(reportId: string): Promise<AiSuggestionsGenerated> {
  return api.post<AiSuggestionsGenerated>(_AI + reportId + '/generate', {}, { showErrorToast: false });
}

// Poll a clustering job (2000 ms cadence in the workbench, per the WTFK frontend-app contract).
export async function getAiSuggestionsProgress(
  reportId: string,
  jobId: string,
): Promise<AiSuggestionsProgress> {
  return api.get<AiSuggestionsProgress>(
    _AI + reportId + '/progress/' + encodeURIComponent(jobId),
    { showErrorToast: false },
  );
}

// Ranked suggestions for a (completed) job. Omit jobId to read the latest job for the report.
export async function listAiSuggestions(reportId: string, jobId?: string): Promise<AiSuggestionsList> {
  const q = jobId ? '?job_id=' + encodeURIComponent(jobId) : '';
  return api.get<AiSuggestionsList>(_AI + reportId + q, { showErrorToast: false });
}

// Record a batch of Match/No-match decisions on suggestions (all-or-nothing). No dollar/side is sent.
export async function decideAiSuggestions(
  reportId: string,
  body: SuggestionDecisionsRequest,
): Promise<{ report_id: string; overlay_count: number; decided: number }> {
  return api.post(_AI + reportId + '/decisions', body, { showErrorToast: false });
}

// The persisted suggestion overlay + GET-only derived preview (informational; never a payable dollar).
export async function getSuggestionOverlay(reportId: string): Promise<SuggestionOverlayState | null> {
  try {
    return await api.get<SuggestionOverlayState>(
      '/api/v1/reconciliation/property-match/suggestion-overlay/' + reportId,
      { showErrorToast: false },
    );
  } catch (err: any) {
    if (err?.status === 404 || err?.response?.status === 404) return null;
    throw err;
  }
}

// ── Manual reconciler — two-sided adjuster/contractor free N:M link + per-line No-Match, carry-forward ──
// Decisions persist by stable line_item_id (NOT run-scoped) and never touch the reconciliation blob
// (money-fence). See backend /manual-match/* endpoints.
export interface ManualLine {
  id: string;
  party: 'adjuster' | 'contractor';
  description: string;
  quantity?: number | string | null;
  unit?: string | null;
  extended_price?: number | string | null;   // verbatim stored money — foot in the UI, never recompute
  building?: string | null;
  location_unit?: string | null;
  room?: string | null;
  category?: string | null;
  coverage_section?: string | null;
}
export interface ManualLink {
  link_id: string;
  adjuster: ManualLine[];
  contractor: ManualLine[];
  decided_by?: string | null;
  decided_role?: string | null;
  decided_at?: string | null;
  _stale?: boolean;
}
export interface ManualNoMatch {
  id: string;
  party: 'adjuster' | 'contractor';
  line: ManualLine | null;
  decided_by?: string | null;
  decided_at?: string | null;
  _stale?: boolean;
}
export interface ManualReconcileState {
  report_id: string;
  adjuster_estimate_id?: string | null;
  contractor_estimate_id?: string | null;
  residue: { adjuster: ManualLine[]; contractor: ManualLine[] };
  links: ManualLink[];
  no_match: ManualNoMatch[];
  counts: {
    adjuster_open: number; contractor_open: number; linked: number; no_match: number;
    stale_links: number; stale_no_match: number;
  };
  input_hash?: string | null;
  run_at?: string | null;
}

const _MM = '/api/v1/reconciliation/property-match/manual-match/';

// Two-sided open residue + manual links + no-matches + four-state counts (read-only). 404 → null.
export async function getManualReconcile(reportId: string): Promise<ManualReconcileState | null> {
  try {
    return await api.get<ManualReconcileState>(_MM + reportId, { showErrorToast: false, timeoutMs: 30000 });
  } catch (err: any) {
    if (err?.status === 404 || err?.response?.status === 404) return null;
    throw err;
  }
}

// Link adjuster line(s) ↔ contractor line(s) as a manual match (free N:M). No dollars sent.
export async function manualLink(
  reportId: string,
  body: { adjuster_ids: string[]; contractor_ids: string[] },
): Promise<{ report_id: string; link: ManualLink; links_count: number }> {
  return api.post(_MM + reportId + '/link', body, { showErrorToast: false });
}

// Mark residue line(s) No Match (per-line). No dollars sent.
export async function manualNoMatch(
  reportId: string,
  items: { id: string; party: 'adjuster' | 'contractor' }[],
): Promise<{ report_id: string; no_match_count: number; decided: number }> {
  return api.post(_MM + reportId + '/no-match', { items }, { showErrorToast: false });
}

// Reverse manual decisions: drop links and/or clear No-Match on line ids.
export async function manualUnresolve(
  reportId: string,
  body: { link_ids?: string[]; clear_no_match_ids?: string[] },
): Promise<{ report_id: string; removed_links: number; cleared_no_match: number }> {
  return api.post(_MM + reportId + '/unresolve', body, { showErrorToast: false });
}

// ── Match review — human Verify/Reject on COMMITTED engine matches + real risk score ──────────────
export interface MatchRiskScore {
  score: number;            // 0..1
  tier: 'high' | 'medium' | 'low';
  cost_discrepancy: number;
  qty_discrepancy: number;
  suspicious_terms: number;
  finbert_component: number;
  model_used: string;       // 'finbert' | 'finbert_unavailable' | 'neutral' | 'finbert_error'
}
export interface MatchReviewRow {
  match_key: string;
  a_lines: LineSnapshot[];
  b_lines: LineSnapshot[];
  confidence?: number | null;
  auto_reconciliation_status?: string | null;
  requires_review?: boolean | null;
  risk: MatchRiskScore;
  decision: 'verified' | 'rejected' | null;
  state: 'verified' | 'rejected' | 'pending';
}
export interface MatchReviewState {
  report_id: string;
  matches: MatchReviewRow[];
  counts: { total: number; verified: number; pending: number; rejected: number };
  risk_tiers: { high: number; medium: number; low: number };
  finbert: boolean;
  input_hash?: string | null;
  run_at?: string | null;
}

// Committed matches with risk + human review state. `finbert` blends the real ProsusAI/finbert
// sentiment (slower; degrades to neutral if the model is unavailable on the backend). 404 → null.
export async function getMatchReview(
  reportId: string,
  finbert = false,
): Promise<MatchReviewState | null> {
  try {
    return await api.get<MatchReviewState>(
      _MM.replace('/manual-match/', '/match-review/') + reportId + (finbert ? '?finbert=1' : ''),
      { showErrorToast: false },
    );
  } catch (err: any) {
    if (err?.status === 404 || err?.response?.status === 404) return null;
    throw err;
  }
}

// Record Verify/Reject on committed matches (batch, reversible). No dollars sent.
export async function decideMatchReview(
  reportId: string,
  items: { match_key: string; decision: 'verified' | 'rejected' }[],
): Promise<{ report_id: string; review_count: number; decided: number }> {
  return api.post(
    _MM.replace('/manual-match/', '/match-review/') + reportId + '/decide',
    { items },
    { showErrorToast: false },
  );
}

// ============================================================================
// FRONT-DOOR clients (P2) — the funnel BEFORE the read/triage views: the adjuster
// uploads two prepared estimates, runs the matcher, then (after review+elect) the
// ATTORNEY promotes the elected L_PROP into the sworn figure. No dollars are entered
// here — the backend ingests prepared estimates and the money-fence lives server-side.
// ============================================================================

const _PM = '/api/v1/reconciliation/property-match/';

// Mirrors backend StoreEstimatesRequest. Precedence per side: file → csv → line_items.
// The adjuster provides the carrier/adjuster estimate + the expert/contractor estimate.
export interface StoreEstimatesRequest {
  carrier_csv?: string | null;
  expert_csv?: string | null;
  carrier_file_b64?: string | null;
  expert_file_b64?: string | null;
  carrier_file_type?: 'pdf' | 'xls' | null;
  expert_file_type?: 'pdf' | 'xls' | null;
  carrier_estimate_id?: string | null;
  expert_estimate_id?: string | null;
}

export interface StoreEstimatesResult {
  report_id: string;
  stored: number;
  carrier_line_count: number;
  expert_line_count: number;
  carrier_sketch_area_count: number;
  expert_sketch_area_count: number;
}

// Store the two estimates (carrier→adjuster side, expert→contractor side). Fixture/clone
// only + write-role only server-side; a bad/unparseable file surfaces as a 422 the UI shows.
export async function storeEstimates(
  reportId: string,
  body: StoreEstimatesRequest,
): Promise<StoreEstimatesResult> {
  return api.post<StoreEstimatesResult>(_PM + 'estimates/' + reportId, body, { showErrorToast: false });
}

export interface RunReconciliationOptions {
  enableSemantic?: boolean;
  // Per-claim responsibility convention (opt-in; default OFF server-side). Persisted on the
  // fixture draft so later runs keep it. e.g. 'condo_master_policy'.
  responsibilityConvention?: string;
}

// Run the matcher on the stored estimates + persist the full reconciliation. Can take seconds.
// Returns the same summary shape as getPropertyReconciliation (heavy detail arrays elided).
export async function runReconciliation(
  reportId: string,
  opts: RunReconciliationOptions = {},
): Promise<PropertyReconciliation> {
  const q: string[] = [];
  if (opts.enableSemantic !== undefined) q.push('enable_semantic=' + String(opts.enableSemantic));
  if (opts.responsibilityConvention) q.push('responsibility_convention=' + encodeURIComponent(opts.responsibilityConvention));
  const qs = q.length ? '?' + q.join('&') : '';
  return api.post<PropertyReconciliation>(_PM + 'run/' + reportId + qs, {}, { showErrorToast: false });
}

export interface PromoteResult {
  report_id: string;
  promoted_l_prop: number | null;
  item_count: number;
  status: 'promoted' | string;
}

// Explicit ATTORNEY act: copy the persisted election's elected L_PROP into the sworn
// data.property_loss. Role-gated server-side (owner/admin/attorney). A 409 means either no
// election to promote yet, or the existing property_loss carries non-default policy modifiers
// the promote refuses to strip — the UI surfaces the 409 detail; it never silently overrides.
export async function promotePropertyElection(reportId: string): Promise<PromoteResult> {
  return api.post<PromoteResult>(_PM + 'promote/' + reportId, {}, { showErrorToast: false });
}

// The adjuster-seat front-door stage, composed CLIENT-SIDE from existing reads (there is no
// single status endpoint): /result (has a run?), /election (elected? stale?). 'promoted' is
// known only to the UI that just promoted — callers pass justPromoted when they hold it.
export type PropertyFrontDoorStage =
  | 'upload'        // S0/S1 — no persisted reconciliation yet
  | 'run'           // estimates implied by a result that has no matches yet (rare) — treat as review-ready
  | 'review'        // S2 — a reconciliation exists, no election yet
  | 'elect'         // alias of review once the human opens the election panel (UI-local)
  | 'elected'       // S3 — an election is persisted (and not stale)
  | 'stale'         // S4 — election exists but the reconciliation was re-run since → re-elect
  | 'promoted';     // S5 — the attorney has sworn the figure (UI-tracked)

export interface PropertyFrontDoorStatus {
  stage: PropertyFrontDoorStage;
  hasReconciliation: boolean;
  hasElection: boolean;
  electionStale: boolean;
  lProp: number | null;
  lPropBasis: string | null;
}

// Pure resolver — no I/O. Pass the already-loaded /result + /election (either may be null).
export function derivePropertyStage(
  reconciliation: PropertyReconciliation | null,
  election: PersistedElection | null,
  justPromoted = false,
): PropertyFrontDoorStatus {
  const hasReconciliation = !!reconciliation;
  const hasElection = !!election;
  const electionStale = !!election?.stale;
  let stage: PropertyFrontDoorStage;
  if (justPromoted) stage = 'promoted';
  else if (!hasReconciliation) stage = 'upload';
  else if (!hasElection) stage = 'review';
  else if (electionStale) stage = 'stale';
  else stage = 'elected';
  return {
    stage,
    hasReconciliation,
    hasElection,
    electionStale,
    lProp: election?.l_prop_payable ?? null,
    lPropBasis: election?.l_prop_basis ?? null,
  };
}
